import { InputField } from "@/components/InputField";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { cn } from "@/lib/utils";
import {
  evolutionCategoriesOrderedSelector,
  evolutionsSelector,
} from "@/redux/evolutions/evolutionsSlice";
import {
  type EvolutionPickerSelection,
  evolutionPickerCounts,
  isSameSelection,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import {
  ChevronDown,
  CircleDollarSign,
  Clock,
  Coins,
  Dna,
  History,
  Layers,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { FilterSidebarItem } from "./EvolutionsBuilder/components/IncludeExcludeEvolutions/FilterSidebarItem";
import {
  evoStateItems,
  getEvoStateIcon,
} from "./EvolutionsOverviewPage/EvolutionsOverviewPage";

interface EvolutionsPickerSidebarProps {
  activeFilter: EvolutionPickerSelection;
  onFilterChange: (filter: EvolutionPickerSelection) => void;
  className?: string;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  hideSearchBar?: boolean;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  showExpired?: boolean;
}

export const EvolutionsPickerSidebar = ({
  activeFilter,
  onFilterChange,
  className,
  searchTerm,
  onSearchChange,
  hideSearchBar,
  headerContent,
  footerContent,
  showExpired,
}: EvolutionsPickerSidebarProps) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const { evolutions, expiredEvolutions } = useSelector(evolutionsSelector);
  const orderedCategories = useSelector(evolutionCategoriesOrderedSelector);

  // Same tallies the mobile panel shows, from the same function.
  const counts = useMemo(
    () => evolutionPickerCounts(evolutions, expiredEvolutions, Date.now()),
    [evolutions, expiredEvolutions],
  );

  const isFilterActive = (filter: EvolutionPickerSelection) =>
    isSameSelection(filter, activeFilter);

  return (
    <ScrollArea
      enableShadows
      shadowDirection="y"
      className={cn(
        "h-full w-72 shrink-0 border-r border-panel-foreground bg-panel-background",
        className,
      )}
    >
      <div className="flex flex-col gap-7 pl-5 py-6 ad-gutter">
        {!hideSearchBar && (
          <div className="mr-5">
            <InputField
              value={searchTerm}
              onChange={onSearchChange}
              dismissSearch={() => onSearchChange("")}
              placeholder={t("picker.searchPlaceholder")}
              type={"search"}
              showSearch={true}
              large={true}
            />
          </div>
        )}

        {headerContent}

        <SidebarSection title={t("picker.byState")}>
          {evoStateItems.map((item) => (
            <FilterSidebarItem
              key={item.key}
              label={t(item.labelKey)}
              count={`(${counts.byState[item.state] ?? 0})`}
              icon={
                <div className="text-dark-secondary">
                  {getEvoStateIcon(item.state, 16)}
                </div>
              }
              isActive={isFilterActive({
                type: "state",
                state: item.state,
              })}
              onClick={() =>
                onFilterChange({ type: "state", state: item.state })
              }
            />
          ))}
          {showExpired && (
            <FilterSidebarItem
              label={t("expired")}
              count={`(${counts.expired})`}
              icon={<History size={16} className="text-dark-secondary" />}
              isActive={isFilterActive({ type: "other", key: "expired" })}
              onClick={() => onFilterChange({ type: "other", key: "expired" })}
            />
          )}
        </SidebarSection>

        <SidebarSection title={t("picker.byCategory")}>
          {orderedCategories.map(
            (category) =>
              counts.byCategory[category.id] && (
                <FilterSidebarItem
                  key={category.id}
                  label={category.name}
                  count={`(${counts.byCategory[category.id] ?? 0})`}
                  icon={<Dna size={16} className="text-dark-tertiary" />}
                  isActive={isFilterActive({
                    type: "category",
                    categoryId: category.id,
                  })}
                  onClick={() =>
                    onFilterChange({
                      type: "category",
                      categoryId: category.id,
                    })
                  }
                />
              ),
          )}
        </SidebarSection>

        <SidebarSection title={t("picker.other")}>
          <FilterSidebarItem
            label={t("picker.newEvolutions")}
            count={`(${counts.newEvolutions})`}
            icon={<Sparkles size={16} className="text-dark-tertiary" />}
            isActive={isFilterActive({ type: "other", key: "new" })}
            onClick={() => onFilterChange({ type: "other", key: "new" })}
          />
          <FilterSidebarItem
            label={t("picker.expiringSoon")}
            count={`(${counts.expiringSoon})`}
            icon={<Clock size={16} className="text-dark-tertiary" />}
            isActive={isFilterActive({ type: "other", key: "expiring" })}
            onClick={() => onFilterChange({ type: "other", key: "expiring" })}
          />
          <FilterSidebarItem
            label={t("common:free")}
            count={`(${counts.free})`}
            icon={<Coins size={16} className="text-dark-tertiary" />}
            isActive={isFilterActive({ type: "other", key: "free" })}
            onClick={() => onFilterChange({ type: "other", key: "free" })}
          />
          <FilterSidebarItem
            label={t("picker.paid")}
            count={`(${counts.paid})`}
            icon={<CircleDollarSign size={16} className="text-dark-tertiary" />}
            isActive={isFilterActive({ type: "other", key: "paid" })}
            onClick={() => onFilterChange({ type: "other", key: "paid" })}
          />
          <FilterSidebarItem
            label={t("picker.all")}
            count={`(${counts.all})`}
            icon={<Layers size={16} className="text-dark-tertiary" />}
            isActive={isFilterActive({ type: "other", key: "all" })}
            onClick={() => onFilterChange({ type: "other", key: "all" })}
          />
        </SidebarSection>

        {footerContent}
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};

// Sidebar filter group with a clickable header that collapses its items.
const SidebarSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between pr-5 text-sm font-medium text-dark-tertiary"
      >
        {title}
        <ChevronDown
          className={cn(
            "size-4 transition-transform duration-200",
            !open && "-rotate-90",
          )}
        />
      </button>
      <Collapsible open={open}>
        <div className="flex flex-col gap-1">{children}</div>
      </Collapsible>
    </div>
  );
};
